import React, { useEffect, useState } from 'react'
import {
  bool,
  arrayOf,
  func,
  number,
  object,
  oneOfType,
  string,
  shape
} from 'prop-types'
import { connect } from 'react-redux'
import { Link } from 'gatsby'
import request from 'graphql-request'

import buildRedirect from '../../../lib/cars/used/expired-redirect-path-builder'

import VehiclePlayer from '../VehiclePlayer'
import VehiclePlayerPanel from '../VehiclePlayerPanel'

import FormReserve from '../ContentForms/Reserve'
import ContentBar from '../ContentBar'
import ContentModal from '../ContentModal'
import ContentModalHowCanWeHelp from '../ContentModalHowCanWeHelp'
import { withFavouritesContext } from '../../context/favourites'
import styled from 'styled-components'
import { Box } from 'rebass'

const PlayerContainer = styled(Box)`
  position: relative;
  &,
  // player container
  & > div > div {
    background-color: ${(props) => props.theme.colors.blue_light};
  }
  // player wrapper
  & > div > div > div {
    padding-top: 0;
  }
`

const VehicleShowcase = ({
  category,
  canPurchase,
  currency,
  details,
  engine,
  expiredDate,
  exteriorImages,
  hasLibraryImages,
  id,
  images,
  isSold,
  location,
  locations,
  modalPaths,
  model,
  offer,
  onOffer,
  onPaymentClick,
  panorama,
  phoneNumber,
  price,
  rawBrand,
  rawModel,
  recent,
  registration,
  reserved,
  vatIncluded,
  context,
  reservable,
  video
}) => {
  const playerProps = {
    exteriorImages,
    images,
    panorama,
    model
  }
  const panelProps = {
    id,
    canPurchase,
    currency,
    details,
    engine,
    hasLibraryImages,
    isSold,
    location,
    model,
    offer,
    onOffer,
    phoneNumber,
    price,
    recent,
    registration,
    vatIncluded,
    reservable,
    category,
    video
  }

  // MODALS
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)
  const [isTestDriveOpen, setIsTestDriveOpen] = useState(false)
  const [isAskQuestionOpen, setIsAskQuestionOpen] = useState(false)
  const [isReserveEnquiryOpen, setIsReserveEnquiryOpen] = useState(false)
  const setPathForModal = (path, triggeredByHistoryChange) => {
    const { pathname } = window.location
    const [modalPathIndex] = modalPaths
      .map((modalPath) => pathname.lastIndexOf(modalPath))
      .filter((index) => index !== -1)

    let newPath = pathname
    let modalPath = ''
    if (modalPathIndex) {
      modalPath = newPath.slice(modalPathIndex)
      newPath = newPath.slice(0, modalPathIndex)
    }
    newPath += (!newPath.endsWith('/') ? '/' : '') + path
    /*
      Pushing state to history should only occur when the event is not
      triggered by a real history change - such as a Back or Forward event.
      This will prevent the state from continuously being overwritten and
      causing the browser to think it is always in the latest position.
      In this case, we should only push history state for modal open and closures.
    */
    if (!triggeredByHistoryChange) {
      window.history.replaceState(null, null, newPath + window.location.search)
    } else if (modalPath) {
      setModalStates(modalPath + window.location.search)
    }
    // window.addEventListener('popstate', handleHistoryBack)
  }

  const setModalStates = (modalPath) => {
    const openTestDrive = modalPath === '/book-a-test-drive'
    const openAskAQuestion = modalPath === '/ask-a-question'
    const openEnquiry = openTestDrive || openAskAQuestion
    const openReserve = modalPath === '/reserve'
    setIsEnquiryOpen(openEnquiry)
    setIsTestDriveOpen(openTestDrive)
    setIsAskQuestionOpen(openAskAQuestion)
    setIsReserveEnquiryOpen(openReserve)
  }

  // ENQUIRY
  const handleEnquiryClose = (triggeredByHistoryChange) => {
    setIsEnquiryOpen(false)
    setIsAskQuestionOpen(false)
    setIsTestDriveOpen(false)
    setPathForModal('', triggeredByHistoryChange)
  }

  // TEST DRIVE
  const handleBookTestDrive = (e) => {
    e.preventDefault()
    setIsTestDriveOpen(true)
    setIsEnquiryOpen(true)
    setPathForModal('book-a-test-drive')
  }
  const handleBookTestDriveClose = () => {
    setIsTestDriveOpen(false)
    setPathForModal('')
  }

  // ASK QUESTION
  const handleAskQuestion = (e) => {
    e.preventDefault()
    setIsAskQuestionOpen(true)
    setIsEnquiryOpen(true)
    setPathForModal('ask-a-question')
  }
  const handleAskQuestionClose = () => {
    setIsAskQuestionOpen(false)
    setPathForModal('')
  }

  // RESERVED
  const [isReserved, setIsReserved] = useState(reserved)
  const updateReserveStatus = async () => {
    const query = `
    {
      reservedVehicles(_id: "${id}") {
        _id
        reservedDate
      }
    }`
    const { reservedVehicles } = await request(
      `${process.env.GATSBY_API_URL}/graphql`,
      query
    )
    setIsReserved(reservedVehicles.length > 0)
  }
  const handleReserveSuccess = () => {
    setIsReserved(true)
  }
  useEffect(() => {
    updateReserveStatus().catch((err) => console.error(err))
  }, [])

  const handleReserveEnquiry = (e) => {
    e.preventDefault()
    setIsReserveEnquiryOpen(true)
    setPathForModal('reserve')
  }

  const handleReserveEnquiryClose = (triggeredByHistoryChange) => {
    setIsReserveEnquiryOpen(false)
    setPathForModal('', triggeredByHistoryChange)
  }

  // PAYMENT
  const handlePaymentClick = (type) => {
    onPaymentClick(type)
  }

  // FAVOURITES
  const [isRecentFavouriteOpen, setIsRecentFavouriteOpen] = useState(false)
  const isFavourited = context.isFavourited(id)
  const handleToggleFavourite = (e) => {
    if (e) e.preventDefault()
    const nextState = !isFavourited
    context.toggle(id)
    setIsRecentFavouriteOpen(nextState)
  }
  const handleRecentFavouriteDismiss = () => {
    setIsRecentFavouriteOpen(false)
  }

  return (
    <>
      {/* NOTIFICATION BARS */}
      {expiredDate && (
        <ContentBar error>
          This vehicle is no longer available.{' '}
          <Link
            to={buildRedirect(category, {
              brand: rawBrand,
              model: rawModel
            })}
          >
            Search for other models like this
          </Link>
          .
        </ContentBar>
      )}

      {offer && offer.sell && (
        <ContentBar offer dismissible>
          <strong>OFFER AVAILABLE: </strong>
          {offer.sell}
        </ContentBar>
      )}

      {isRecentFavouriteOpen && (
        <ContentBar
          success
          dismissible
          autodismiss
          sticky
          onDismiss={handleRecentFavouriteDismiss}
        >
          This vehicle has been added to your{' '}
          <strong>
            <a
              href={`${process.env.GATSBY_PORTAL_URL}/favourites`}
              target="_blank"
              rel="noreferrer"
            >
              favourites list
            </a>
          </strong>
        </ContentBar>
      )}

      {/* PLAYER */}
      <PlayerContainer>
        <VehiclePlayer {...playerProps}>
          <VehiclePlayerPanel
            {...panelProps}
            favourite={isFavourited}
            onToggleFavourite={handleToggleFavourite}
            isReserved={isReserved}
            onReserveEnquiryClick={handleReserveEnquiry}
            onPaymentClick={handlePaymentClick}
            onBookTestDriveClick={handleBookTestDrive}
            onAskQuestionClick={handleAskQuestion}
          />
        </VehiclePlayer>
      </PlayerContainer>

      {/* MODALS */}
      <ContentModalHowCanWeHelp
        open={isEnquiryOpen}
        onClose={handleEnquiryClose}
        vehicleId={id}
        locations={locations}
        isTestDriveOpen={isTestDriveOpen}
        onTestDriveOpen={() => setPathForModal('book-a-test-drive')}
        onTestDriveClose={handleBookTestDriveClose}
        location={location && location.shortName}
        registration={registration}
        isAskQuestionOpen={isAskQuestionOpen}
        onAskQuestionOpen={() => setPathForModal('ask-a-question')}
        onAskQuestionClose={handleAskQuestionClose}
        locationInfo={location}
        brand={rawBrand}
        model={rawModel}
      />
      <ContentModal
        title="Reserve this car"
        subtitle={
          price.reserve &&
          `For only £${price.reserve.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          })}`
        }
        open={isReserveEnquiryOpen}
        onClose={handleReserveEnquiryClose}
      >
        <FormReserve
          handleReserveSuccess={handleReserveSuccess}
          vehicleId={id}
          locationInfo={location}
          brand={rawBrand}
          model={rawModel}
        />
      </ContentModal>

      {/* <ContentModal
        title={info.title}
        slim
        open={isInfoOpen}
        onClose={handleInfoClose}
        action={info.action}
      >
        {info.children}
      </ContentModal> */}
    </>
  )
}

VehicleShowcase.propTypes = {
  category: string,
  currency: object,
  details: arrayOf(
    shape({
      label: string,
      value: oneOfType([number, string])
    })
  ),
  engine: string,
  expiredDate: string,
  exteriorImages: arrayOf(string),
  hasLibraryImages: bool,
  id: string,
  images: arrayOf(string),
  isSold: bool,
  location: object,
  locations: arrayOf(string),
  model: string,
  offer: object,
  onOffer: bool,
  onPaymentClick: func,
  panorama: string,
  phoneNumber: object,
  price: object,
  rawBrand: string,
  rawModel: string,
  recent: bool,
  registration: string,
  reserved: bool,
  vatIncluded: bool,
  context: object,
  video: string
}

export default connect((state) => ({ phoneNumber: state.phoneNumber }))(
  withFavouritesContext(VehicleShowcase)
)
